import type { InputFeatureV1 } from '@/sdk/model/InputFeatureV1';
import type { InstanceOutputV1 } from '@/sdk/model/InstanceOutputV1';
import type { ModelApplicationMappingV1 } from '@/sdk/model/ModelApplicationMappingV1';

/**
 * Carries forward an instance's input mappings onto a model's input features for
 * rehydrating the model-apply tool during edit or upgrade.
 *
 * Matches features by canonicalId first (stable across model versions), then falls
 * back to feature id. Unmatched features are returned with no input values.
 */
export const mapInstanceInputsToFeatures = (
  instanceData: InstanceOutputV1,
  modelInputFeatures: InputFeatureV1[],
): InputFeatureV1[] => {
  const inputsByFeatureId = new Map((instanceData.inputs ?? []).map((input) => [input.featureId, input]));
  const inputsByCanonicalId = new Map(
    (instanceData.inputFeatures ?? [])
      .filter((f) => f.canonicalId && f.id)
      .map((f) => [f.canonicalId!, inputsByFeatureId.get(f.id!)]),
  );

  return modelInputFeatures.map((feature) => {
    const instanceInput =
      (feature.canonicalId && inputsByCanonicalId.get(feature.canonicalId)) || inputsByFeatureId.get(feature.id);
    // Smuggles inputItem, inputNumeric, etc. onto the InputFeatureV1 shape.
    // model.store.ts setModelOutput reads them as ModelInput, which declares those fields.
    return { ...feature, ...extractInputValues(instanceInput, feature.name ?? '') };
  });
};

const extractInputValues = (instanceInput: ModelApplicationMappingV1 | undefined, featureName: string) => {
  const values: Record<string, string | number | boolean> = { name: featureName };
  if (instanceInput?.itemIdInput !== undefined) values.inputItem = instanceInput.itemIdInput;
  if (instanceInput?.numericInput !== undefined) values.inputNumeric = instanceInput.numericInput;
  if (instanceInput?.numericInputUom !== undefined) values.inputNumericUom = instanceInput.numericInputUom;
  if (instanceInput?.textInput !== undefined) values.inputText = instanceInput.textInput;
  if (instanceInput?.booleanInput !== undefined) values.inputBoolean = instanceInput.booleanInput;
  return values;
};
